import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import useDownloadTicket from '@/hooks/useDownloadTicket';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { getBookingFulfillments } from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import { Button, Icons, Popover, Sheet } from '@packages/webshop-design-system';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

const TicketsDownload: FC = () => {
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const profile = useSelector(profileDataSelector);
  const booking = useSelector(manageFlowBookingSelector);

  const data = {
    bookingId: booking?.id,
    code: booking?.code,
    payerEmail: profile?.email,
    fulfillments: getBookingFulfillments(booking),
  };

  const {
    isDownloading,
    isPkPassDownloading,
    handleTicketDownload,
    handlePkPassDownload,
  } = useDownloadTicket(data);

  function renderDownloadButton() {
    return (
      <Button
        data-testid="profile-booking-download"
        variant="secondary"
        size={isLaptopOrBigger ? 'regular' : 'large'}
        className="group"
        startAdornment={<Icons.download width={16} height={16} />}
        endAdornment={
          <Icons.chevronDown
            height={16}
            width={16}
            className="transition-transform duration-200 group-aria-expanded:rotate-180"
          />
        }
      >
        <TransText i18nKey="download" />
      </Button>
    );
  }

  function renderDownloadContent() {
    return (
      <div className="flex flex-col gap-2 p-3 laptop:p-0">
        <Button
          data-testid="profile-download-tickets"
          variant="secondary"
          size={isLaptopOrBigger ? 'regular' : 'large'}
          isPending={isDownloading}
          onPress={handleTicketDownload}
          startAdornment={<Icons.download height={16} width={16} />}
        >
          <TransText i18nKey="downloadTickets" />
        </Button>
        <Button
          data-testid="profile-add-to-wallet"
          variant="secondary"
          size={isLaptopOrBigger ? 'regular' : 'large'}
          isPending={isPkPassDownloading}
          onPress={handlePkPassDownload}
          startAdornment={<Icons.pkPassWallet height={16} width={16} />}
        >
          <TransText i18nKey="addToWallet" />
        </Button>
      </div>
    );
  }

  if (isLaptopOrBigger) {
    return (
      <Popover
        trigger={renderDownloadButton()}
        content={renderDownloadContent()}
        data-testid="tickets-download-popover"
        className={{ content: 'flex flex-col gap-2 p-4' }}
      />
    );
  }

  return (
    <Sheet
      trigger={renderDownloadButton()}
      side="bottom"
      headerIcon={
        <Icons.download width={20} height={20} className="text-primary" />
      }
      headerTitle={<TransText i18nKey="download" />}
      className={{ content: 'p-0' }}
      data-testid="tickets-download-sheet"
    >
      {renderDownloadContent()}
    </Sheet>
  );
};

export default TicketsDownload;
